import {
    NavigationBar as NavigationBarShared,
    SiteNavItemPropsList,
    SiteNavProps,
    useAnalyticsActionTracker,
    useAuthentication,
} from '@cp-shared-15/frontend-ui';
import { TFunction } from 'i18next';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { useLogin } from '../../auth/useLogin';
import { isFirstRegistrationLocalStorageKey } from '../../config';
import { getCurrentPageName } from '../../utils';
import {
    autoLogoutPath,
    contactDetailsHookPagePath,
    dashboardPagePath,
    faqPagePath,
    introductionPagePath,
    landingPagePath,
    legalNoticePath,
    manualLogoutPath,
    myProfilePagePath,
    myRequestsPath,
    notAuthorizedPagePath,
    notFoundPagePath,
    registrationPagePath,
    thirdPartyLicensesPagePath,
} from './paths';

const pagesWithoutNavbar = [registrationPagePath(), contactDetailsHookPagePath()];

const pagesWithoutStaticHeader = [
    landingPagePath(),
    introductionPagePath(),
    faqPagePath(),
    legalNoticePath(),
    notAuthorizedPagePath(),
    notFoundPagePath(),
    registrationPagePath(),
    thirdPartyLicensesPagePath(),
    autoLogoutPath(),
    manualLogoutPath(),
];

function publicNavigationLinks(t: TFunction, onLogin: () => Promise<void>): SiteNavItemPropsList {
    return [
        {
            label: t('login'),
            href: '#',
            onClick: onLogin,
        },
    ];
}

function privateNavigationLinks(t: TFunction, onLogout: () => void): SiteNavItemPropsList {
    return [
        {
            url: dashboardPagePath(),
            href: dashboardPagePath(),
            label: t('dashboard'),
        },
        {
            url: myProfilePagePath(),
            href: myProfilePagePath(),
            label: t('profile'),
        },
        {
            url: myRequestsPath(),
            href: myRequestsPath(),
            label: t('requests'),
        },
        {
            url: faqPagePath(),
            href: faqPagePath(),
            label: t('faq'),
        },
        {
            label: t('logout'),
            href: '#',
            onClick: onLogout,
        },
    ];
}

export const NavigationBar: React.FC = () => {
    const { t: cms } = useTranslation('navigation-cms');

    const history = useHistory();
    const location = useLocation();
    const { isAuthenticated, logout } = useAuthentication();
    const login = useLogin();

    const currentPageName = getCurrentPageName(location);

    const { onAction: onLoginAction } = useAnalyticsActionTracker('login');
    const { onAction: onLogoutAction } = useAnalyticsActionTracker('logout');

    const navigationItems = (isAuthenticated
        ? privateNavigationLinks(cms, () => {
              window.localStorage.removeItem(isFirstRegistrationLocalStorageKey);
              onLogoutAction(currentPageName);
              return logout({ redirectUri: window.location.origin + manualLogoutPath() });
          })
        : publicNavigationLinks(cms, () => {
              window.localStorage.removeItem(isFirstRegistrationLocalStorageKey);
              onLoginAction(currentPageName);
              return login();
          })
    ).map(navItem => ({
        ...{ isActive: history.location.pathname === navItem.url },
        ...navItem,
    }));

    const currentPathName = history.location.pathname;

    const hidden = pagesWithoutNavbar.includes(currentPathName);
    const withStaticHeader = !pagesWithoutStaticHeader.includes(currentPathName);

    const navigation: SiteNavProps = {
        navigationItems,
    };

    const logoRedirectPath = isAuthenticated ? dashboardPagePath() : landingPagePath();

    const handleNavClick = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            const anchor = (e.target as HTMLElement).closest<HTMLAnchorElement>('a[href]');
            if (!anchor) return;
            const href = anchor.getAttribute('href');
            if (!href || href === '#' || /^https?:\/\//.test(href)) return;
            e.preventDefault();
            if (history.location.pathname !== href) {
                history.push(href);
            }
        },
        [history],
    );

    return (
        <div onClick={handleNavClick}>
            <NavigationBarShared
                logoAltText={cms(`${isAuthenticated ? 'dashboard' : 'home'}`)}
                navigation={navigation}
                onLogoClickPath={logoRedirectPath}
                onLogoClick={() => history.push(logoRedirectPath)}
                withStaticHeader={withStaticHeader}
                hidden={hidden}
            />
        </div>
    );
};
